import type { DerivedValue } from "@-/com.npmjs.react-native-reanimated";
import { createContext } from "react";

/** The sheet's Log In / Sign Up toggle. */
export type AuthSheetMode = "signIn" | "signUp";

/**
 * What the Sign Up email fields do.
 *
 * - `getStarted`: record the address on the device and continue as a guest (no password yet).
 * - `account`: create the account with email + password. Used to activate an address a guest
 *   recorded earlier, and wherever a caller explicitly asks for sign-up.
 */
export type AuthSheetSignUpKind = "account" | "getStarted";

export type AuthSheetRequest = {
  email: string;
  mode: AuthSheetMode;
  /** Each opening starts with fresh form state. */
  openCount: number;
  signUpKind: AuthSheetSignUpKind;
};

/** Where an auth sheet was opened from, reported as the `source` of `auth_sheet_opened`. */
export type AuthSheetSource =
  | "account_settings"
  | "profile_card"
  | "settings_profile"
  | "slide_footer"
  | "unknown"
  | "welcome";

export interface AuthSheetApi {
  /** Closes the auth sheet, if it is open. */
  close: () => void;
  /**
   * Opens the auth sheet for an account, with the device's account email filled in. Without a
   * `mode`, an anonymous local account (an address recorded through "Get Started") gets Sign Up,
   * activating it, and everyone else (including a device whose session expired) gets Log In.
   */
  openAccount: (options?: { mode?: AuthSheetMode; source?: AuthSheetSource }) => void;
  /** Opens the auth sheet on Sign Up: social sign-in, or continue anonymously with just an email. */
  openGetStarted: (options?: { source?: AuthSheetSource }) => void;
  /**
   * How far the sheet is presented, following it as it slides and is dragged: 0 while closed, 1
   * while open.
   */
  presentation: DerivedValue<number>;
  /** Web's welcome card hosts the requested form in place; other entry points use the sheet. */
  welcomeRequest: AuthSheetRequest | null;
}

export const AuthSheetContext = createContext<AuthSheetApi | null>(null);
